import React, { useRef, useState, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import styles from "../ResourcesPlanner.module.scss";
import { useView } from "../plannerContext";
import moment from "moment";
import {
  getLabelByType,
  getTaskPriorityPillClass,
  getTaskStatusPillClass,
  TimelineTaskIcons,
} from "../../../helpers/utils";

export default function FloatingAllocationCard({ task, scope, project, allocation, position, onClose, id }) {
  const showAllocationHours = useView((state) => state.showHours);
  // An allocation with no hours means the resource is assigned to the task without an effort estimate.
  const hasNoHours = !allocation?.hoursPerDay && !allocation?.totalHours;
  const cardRef = useRef(null);
  const [pos, setPos] = useState({ x: position.x, y: position.y });
  const dragRef = useRef({ dragging: false, offsetX: 0, offsetY: 0 });

  const handleMouseDown = useCallback((e) => {
    if (e.target.closest(`.${styles.floatingCardClose}`)) return;
    e.preventDefault();
    dragRef.current = {
      dragging: true,
      offsetX: e.clientX - pos.x,
      offsetY: e.clientY - pos.y,
    };

    const handleMouseMove = (moveEvent) => {
      if (!dragRef.current.dragging) return;
      setPos({
        x: moveEvent.clientX - dragRef.current.offsetX,
        y: moveEvent.clientY - dragRef.current.offsetY,
      });
    };

    const handleMouseUp = () => {
      dragRef.current.dragging = false;
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  }, [pos]);

  // Clamp position to viewport on mount
  useEffect(() => {
    if (cardRef.current) {
      const rect = cardRef.current.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      let newX = pos.x;
      let newY = pos.y;
      if (newX + rect.width > vw) newX = vw - rect.width - 10;
      if (newY + rect.height > vh) newY = vh - rect.height - 10;
      if (newX < 0) newX = 10;
      if (newY < 0) newY = 10;
      if (newX !== pos.x || newY !== pos.y) setPos({ x: newX, y: newY });
    }
  }, []);

  return createPortal(
    <div
      ref={cardRef}
      className={styles.floatingCard}
      style={{
        position: "fixed",
        left: `${pos.x}px`,
        top: `${pos.y}px`,
        zIndex: 1100,
      }}
      onMouseDown={handleMouseDown}
    >
      <div className={styles.floatingCardHeader}>
        <div className={styles.floatingCardTitle}>
          <div className="flex-shrink-0 w-3 h-3 flex items-center justify-center mr-1">
            {TimelineTaskIcons[task?.status]?.()}
          </div>
          <span>{task?.name}</span>
        </div>
        <div className={styles.floatingCardActions}>
          {showAllocationHours && (
            hasNoHours ? (
              <span className={`${styles.allocationPill} ${styles.pillNormal}`}>Assigned</span>
            ) : (
              <>
                <span className={`${styles.allocationPill} ${styles.pillNormal}`}>
                  {allocation?.hoursPerDay} h/d
                </span>
                <span className={`${styles.allocationPill} ${styles.totalPillNormal}`}>
                  {allocation?.totalHours} hrs
                </span>
              </>
            )
          )}
          <button className={styles.floatingCardClose} onClick={() => onClose(id)}>
            <X size={14} />
          </button>
        </div>
      </div>

      <div className={styles.floatingCardBody}>
        {task?.refId && (
          <div className={styles.popoverSection}>
            <span className={styles.popoverLabel}>Change Ticket</span>
            <span className={styles.popoverValue}>{task.refId}</span>
          </div>
        )}

        <div className={styles.popoverSection}>
          <span className={styles.popoverLabel}>Project</span>
          <span className={styles.popoverValue}>{project?.name || "—"}</span>
        </div>

        <div className={styles.popoverSection}>
          <span className={styles.popoverLabel}>Scope</span>
          <span className={styles.popoverValue}>{scope?.name || "—"}</span>
        </div>

        <div className={styles.popoverDivider} />

        <div className={styles.popoverSection}>
          <span className={styles.popoverLabel}>Start Date</span>
          <span className={styles.popoverValue}>
            {allocation?.startDate ? moment(allocation.startDate).format("MMM D, YYYY") : "—"}
          </span>
        </div>

        <div className={styles.popoverSection}>
          <span className={styles.popoverLabel}>End Date</span>
          <span className={styles.popoverValue}>
            {allocation?.endDate ? moment(allocation.endDate).format("MMM D, YYYY") : "—"}
          </span>
        </div>

        <div className={styles.popoverSection}>
          <span className={styles.popoverLabel}>Priority</span>
          <span className={styles.popoverValue}>
            <p
              className={`${getTaskPriorityPillClass(task?.priority ?? 0)}`}
              style={{ fontSize: "0.75rem", lineHeight: "1rem", fontWeight: "500" }}
            >
              {getLabelByType("taskPriority", task?.priority ?? 0)}
            </p>
          </span>
        </div>

        <div className={styles.popoverSection}>
          <span className={styles.popoverLabel}>Status</span>
          <span className={styles.popoverValue}>
            <p
              className={`${getTaskStatusPillClass(task?.status ?? 0)}`}
              style={{ fontSize: "0.75rem", lineHeight: "1rem", fontWeight: "500" }}
            >
              {getLabelByType("taskStatus", task?.status ?? 0)}
            </p>
          </span>
        </div>
      </div>
    </div>,
    document.body
  );
}
